<template>
    <div v-if="event">
        <div class="row">
            <div class="col-xl-6">
                <b-card :title="event.name" :img-src="getImage()" img-alt="Image" img-top bg-variant="dark" text-variant="light" class="m-b-20">
                    <b-card-text>{{ event.description }} </b-card-text>
                </b-card>
            </div>
            <div class="col-xl-6">
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Details</h6>
                    </template>
                    <b-card-text>
                        <b>Name: </b><span>{{ event.name }}</span><br>
                        <b>Start date &amp; time: </b><span>{{ formatTime(event.startTime) }}</span><br>
                        <b>End date &amp; time: </b><span>{{ formatTime(event.endTime) }}</span><br>
                        <b>Set up date &amp; time: </b><span>{{ formatTime(event.setupTime) }}</span><br>
                        <b>Take down date &amp; time: </b><span>{{ formatTime(event.takeDownTime) }}</span><br>
                    </b-card-text>
                </b-card>
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Data</h6>
                    </template>
                    <b-card-text>
                        <h4>Event bank</h4>
                        <b-row>
                            <b-col>
                                <h5>Space</h5>
                                <b>Venues: </b><span>{{ venues }}</span><br>
                                <b>Areas: </b><span>{{ areas }}</span><br>
                                <b>Space: </b><span>{{ spaces }}</span><br><br>
                                <h5>Stuff</h5>
                                <b>Main categories: </b><span>{{ mainCategories }}</span><br>
                                <b>Subcategories: </b><span>{{ subcategories }}</span><br>
                                <b>Stuff: </b><span>{{ stuff }}</span><br>
                            </b-col>
                            <b-col>
                                <h5>Staff</h5>
                                <b>Groups: </b><span>{{ groups }}</span><br>
                                <b>Teams: </b><span>{{ teams }}</span><br>
                                <b>Staff: </b><span>{{ staff }}</span><br><br>
                                <h5>Service</h5>
                                <b>Service categories: </b><span>{{ serviceCategories }}</span><br>
                                <b>Service areas: </b><span>{{ serviceAreas }}</span><br>
                                <b>Services: </b><span>{{ services }}</span><br>
                            </b-col>
                        </b-row>
                        <hr>
                        <h4>Planning</h4>
                        <b>Storyboards: </b><span>{{ storyboards.length }}</span><br>
                        <b>Stages: </b><span>{{ stages.length }}</span><br>
                        <b>Steps: </b><span>{{ steps.length }}</span>
                    </b-card-text>
                </b-card>
                <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
                    <template v-slot:header>
                        <h6 class="mb-0">Notes</h6>
                    </template>
                    <b-card-text>
                        <vue-custom-scrollbar class="height-sm">
                            <ul class="media-list media-list-with-divider media-messaging">
                                <li class="media media-sm" v-for="(note, index) in event.notes" :key="note.id">
                                    <div class="media-body">
                                        <h5 class="media-heading">Note #{{ index + 1 }}</h5>
                                        <p>{{ note.content }}</p>
                                    </div>
                                </li>
                                <li v-if="!event.notes.length" class="media media-sm">
                                    <div class="media-body">
                                        <p>No notes</p>
                                    </div>
                                </li>
                            </ul>
                        </vue-custom-scrollbar>
                    </b-card-text>
                </b-card>
            </div>
        </div>
        <b-card class="m-b-20" header-tag="header" header-bg-variant="dark" header-text-variant="light">
            <template v-slot:header>
                <h6 class="mb-0">Actions</h6>
            </template>
            <url-button class="btn-secondary" label="Back to list" :url="'/event'" />
            <b-button v-b-modal.modalAlert class="btn btn-danger pull-right">Delete</b-button>
            <b-button v-b-modal.modalCopy class="btn-white pull-right mr-1">Make a copy</b-button>
            <url-button class="btn-yellow pull-right" label="Edit" :url="`/event/${$route.params.id}/edit`" />
        </b-card>

        <b-modal id="modalCopy" ref="modalCopy" centered title="Make a copy" cancel-variant="white" ok-variant="yellow" ok-title="Confirm" v-on:ok="copy" :ok-disabled="disabled">
            <b-overlay :show="show" rounded="sm">
                <template v-slot:overlay>
                    <div class="text-center">
                        <b-spinner />
                        <p>Copying event</p>
                    </div>
                </template>
                <div class="alert alert-secondary m-b-0">
                    <h5><i class="fa fa-info-circle"></i>Make a copy</h5>
                    <p>Are you sure you want to make a copy of {{ event.name }}</p>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>Name:</b></label>
                        <div class="col-md-9">
                            <input @input="buttonStatus" v-model="copyName" type="text" class="form-control m-b-5" placeholder="Enter new name" />
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>Start date &amp; time:</b></label>
                        <div class="col-md-9">
                            <b-form-datepicker v-model="startDate" @input="checkTimes" :min="setupDate ? setupDate : min" :max="endDate ? endDate : null" class="form-control m-b-5" />
                    <b-form-timepicker v-model="startDateTime" @input="checkTimes" class="form-control m-b-5" />
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>End date &amp; time:</b></label>
                        <div class="col-md-9">
                            <b-form-datepicker v-model="endDate" @input="checkTimes" :min="startDate" :max="takeDownDate ? takeDownDate : null" class="form-control m-b-5" />
                    <b-form-timepicker v-model="endDateTime" @input="checkTimes" class="form-control m-b-5" />
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>Set up date &amp; time:</b></label>
                        <div class="col-md-9">
                            <b-form-datepicker v-model="setupDate" @input="checkTimes" :min="min" :max="startDate ? startDate : null" class="form-control m-b-5" />
                    <b-form-timepicker v-model="setupDateTime" @input="checkTimes" class="form-control m-b-5" />
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>Take down date &amp; time:</b></label>
                        <div class="col-md-9">
                            <b-form-datepicker v-model="takeDownDate" @input="checkTimes" :min="endDate" class="form-control m-b-5" />
                    <b-form-timepicker v-model="takeDownDateTime" @input="checkTimes" class="form-control m-b-5" />
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3"><b>Description:</b></label>
                        <div class="col-md-9">
                            <textarea v-model="description" class="form-control" rows="3"></textarea>
                        </div>
                    </div>
                </div>
            </b-overlay>
        </b-modal>

        <b-modal id="modalAlert" :title="'Delete ' + event.name" centered cancel-variant="white" ok-variant="danger" ok-title="Delete" v-on:ok="deleteEvent">
            <div class="alert alert-danger m-b-0">
                <h5><i class="fa fa-info-circle"></i>Delete</h5>
                <p>Are you sure you want to delete {{ event.name }}?</p>
                <p>You will lose everything in this event.</p>
            </div>
        </b-modal>

        <b-modal ref="modalSuccess" title="Success!" centered ok-only ok-variant="yellow">
            <div class="alert alert-success m-b-0">
                <h5><i class="fa fa-info-circle"></i>Success</h5>
                <p>Event copied successfully!</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import UrlButton from '../../components/buttons/UrlButton.vue'
import moment from 'moment'

export default {
    name: 'EventShow',
    props: ['event', 'events'],
    components: {
        UrlButton
    },
    data() {
        return {
            copyName: null,
            minTime: null,
            min: null,
            startTime: null,
            startDate: null,
            startDateTime: null,
            endTime: null,
            endDate: null,
            endDateTime: null,
            setupTime: null,
            setupDate: null,
            setupDateTime: null,
            takeDownTime: null,
            takeDownDate: null,
            takeDownDateTime: null,
            description: null,
            disabled: false,
            show: false,
            venues: 0,
            areas: 0,
            spaces: 0,
            mainCategories: 0,
            subcategories: 0,
            stuff: 0,
            groups: 0,
            teams: 0,
            staff: 0,
            serviceCategories: 0,
            serviceAreas: 0,
            services: 0
        }
    },
    computed: {
        ...mapState('item', ['items']),
        ...mapState('category', ['categories']),
        ...mapState('storyboard', ['storyboards']),
        ...mapState('stage', ['stages']),
        ...mapState('step', ['steps'])
    },
    methods: {
        ...mapActions('item', ['getItems']),
        ...mapActions('category', ['getCategories']),
        ...mapActions('storyboard', ['getStoryboards']),
        ...mapActions('stage', ['getStages']),
        ...mapActions('step', ['getSteps']),
        formatTime(time){
          return moment(time, [moment.HTML5_FMT.DATETIME_LOCAL]).format("DD.MM.YYYY HH:mm");
        },
        getImage() {
            if (this.event.image) {
                return process.env.VUE_APP_API_URL + '/uploads/images/' + this.event.image.imageFd;
            } else {
                return require('../../assets/images/no_image.jpg');
            }
        },
        getCounts() {
            this.venues = 0;
            this.areas = 0;
            this.spaces = 0;
            this.mainCategories = 0;
            this.subcategories = 0;
            this.stuff = 0;
            this.groups = 0;
            this.teams = 0;
            this.staff = 0;
            this.serviceCategories = 0;
            this.serviceAreas = 0;
            this.services = 0;
            this.categories.forEach(category => {
                switch (category.type.id) {
                    case 1:
                        if (category.parent) {
                            this.serviceAreas++;
                        } else {
                            this.serviceCategories++;
                        }
                        break;
                    case 2:
                        if (category.parent) {
                            this.areas++;
                        } else {
                            this.venues++;
                        }
                        break;
                    case 3:
                        if (category.parent) {
                            this.teams++;
                        } else {
                            this.groups++;
                        }
                        break;
                    case 4:
                        if (category.parent) {
                            this.subcategories++;
                        } else {
                            this.mainCategories++;
                        }
                        break;
                    default:
                        break;
                }
            });

            this.items.forEach(item => {
                switch (item.type.id) {
                    case 1:
                        this.services++;
                        break;
                    case 2:
                        this.spaces++;
                        break;
                    case 3:
                        this.staff++;
                        break;
                    case 4:
                        this.stuff++;
                        break;
                    default:
                        break;
                }
            });
        },
        buttonStatus() {
            if (this.copyName == '' || this.copyName == null) {
                this.disabled = true;
            } else {
                this.disabled = false;
            }
        },
        combineDateAndTime(date, time) {
            let dateTime = null;
            if (!date) {
                return dateTime;
            } else if (!time) {
                dateTime = new Date(date);
            } else {
                dateTime = new Date(date + ' ' + time);
            }

            let day = dateTime.getDate(),
                month = dateTime.getMonth() + 1,
                year = dateTime.getFullYear(),
                hours = dateTime.getHours(),
                minutes = dateTime.getMinutes();

            if (day < 10) {
                day = "0" + day;
            }

            if (month < 10) {
                month = "0" + month;
            }

            if (hours < 10) {
                hours = "0" + hours;
            }

            if (minutes < 10) {
                minutes = "0" + minutes;
            }

            return year + "-" + month + "-" + day + "T" + hours + ":" + minutes;
        },
        checkTimes() {
            this.startTime = this.combineDateAndTime(this.startDate, this.startDateTime);
            this.endTime = this.combineDateAndTime(this.endDate, this.endDateTime);
            this.setupTime = this.combineDateAndTime(this.setupDate, this.setupDateTime);
            this.takeDownTime = this.combineDateAndTime(this.takeDownDate, this.takeDownDateTime);
        },
        copy(bvModalEvt) {
            bvModalEvt.preventDefault();
            this.show = true;
            let imageId = null;
            if (this.event.image) {
                imageId = this.event.image.id;
            }
            this.$parent.copy(this.event.id, JSON.stringify({
                name: this.copyName,
                startTime: this.startTime,
                endTime: this.endTime,
                setupTime: this.setupTime,
                takeDownTime: this.takeDownTime,
                description: this.description,
                imageId: parseInt(imageId),
                notes: this.event.notes,
                company: this.event.company
            }));
        },
        deleteEvent() {
            if (this.event.image) {
                this.$parent.removeImage(this.event.image.imageId);
            }
            this.$parent.delete(this.event.id);
        }
    },
    mounted() {
        this.getStoryboards(this.event.id);
        this.getStages(this.event.id);
        this.getSteps(this.event.id);
        this.getCategories(this.event.id);
        this.getItems(this.event.id);
    },
    watch: {
        event: function() {
            let name = this.event.name;
            if (name.match(/-[0-9]+(?!.*[0-9])/)) {
                this.copyName = name.replace(/[0-9]+(?!.*[0-9])/, function(match) {
                    return parseInt(match) + 1;
                });
            } else {
                this.copyName = name + "-1";
            }
            this.startTime = this.event.startTime;
            this.endTime = this.event.endTime;
            this.setupTime = this.event.setupTime;
            this.takeDownTime = this.event.takeDownTime;
            this.description = this.event.description;
            this.minTime = new Date().toISOString().substr(0, 16);
            this.min = this.minTime;
        },
        events: function() {
            if (this.show) {
                this.show = false;
                this.$refs['modalCopy'].hide();
                this.$refs['modalSuccess'].show();
            }
        },
        items: function() {
            this.getCounts();
        },
        categories: function() {
            this.getCounts();
        }
    }
}
</script>
